import { Navigate } from 'react-router-dom';

import { ROUTES } from '../constants/routes';
import { STAFF_ROLES } from '../constants/staff';
import { usePermissions } from '../context/PermissionContext';

/**
 * isAdminRole — the ONE "is this viewer an admin?" predicate, shared by the
 * route guard below and by the dashboard's Patients tile, so a hidden tile
 * and a blocked route can never disagree about the same viewer.
 *
 * FAILS CLOSED. `role` is `null` for any session PermissionContext could not
 * map (`deriveRoleFromSession`, bk-793 / F2) and for a session whose backend
 * response carries `permissions` without `role`. Both must read as NOT admin,
 * so the check is an exact comparison against the shared STAFF_ROLES.ADMIN
 * constant on a normalised string — never a truthiness or loose check that a
 * null/undefined/non-string value could slip through.
 *
 * @param {string|null|undefined} role
 * @returns {boolean}
 */
export function isAdminRole(role) {
  return String(role ?? '').toUpperCase() === STAFF_ROLES.ADMIN;
}

/**
 * AdminRoleGate — route guard for the admin-only patient card + directory
 * (EPIC PATIENT-CARD, D7). Twin of AdmissionEnabledGate: it composes INSIDE
 * a ProtectedRoute and sends everyone else to the org home, the same
 * not-authorised surface every other gate in App.js uses.
 *
 * Gates on the ROLE, never on a permission slug: ADMIN holds FULL_ACCESS
 * server-side, which short-circuits every permission check, so no slug can
 * express "admin but not doctor" (D7). Do not "fix" this to hasPermission()
 * to match its neighbours.
 *
 * This gate is USER EXPERIENCE, not security — it stops a non-admin walking
 * into a wall. The real authorisation boundary is the server: every patient
 * card endpoint carries its own ADMIN role dependency and refuses whatever
 * the browser chose to render.
 */
export default function AdminRoleGate({ children }) {
  const { role } = usePermissions();
  if (!isAdminRole(role)) return <Navigate to={ROUTES.ORG} replace />;
  return children;
}
